import { GridEditingState } from '@mui/x-data-grid-premium';
import { useMutation } from '@redwoodjs/web';
import { useLocation } from '@tanstack/react-router';
import _ from 'lodash';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { AbortSignal, useDebounce, useDebounceByKey } from 'src/hooks/useDebounce';
import { FieldCommitHandlers, UseFieldCommitOptions, useFieldCommit } from 'src/hooks/useFieldCommit';
import type { GridApiRef } from 'src/modules/grids';
import { v4 } from 'uuid';
import { createStore, StoreApi, useStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { StateCreatorWithImmer } from './StoreStateProvider';

type EditStateType = 'transition' | 'edit' | 'debounced';

type EditingState = {
  pushEditState: (type: EditStateType, id: string) => void;
  releaseEditState: (type: EditStateType, id: string) => void;
  getIsSavingEdits: () => boolean;
  getIsTransitioning: () => boolean;
  getIsActive: () => boolean;
  getHasRowEdits: () => boolean;
} & {
  [key in EditStateType]: Set<string>;
} & GridEditingStore;

type GridEditingStore = {
  rowEditing: Map<string, { initial: GridEditingState; final: GridEditingState }>;
  setStopRowEditCallback: (id: string, callback: () => void) => void;
  stopRowEditCallbacks: Map<string, () => void>;
  cleanupStopRowEditCallbacks: (gridKey: string) => void;
  pushGridEditState: (id: string, state: GridEditingState) => void;
  releaseGridEditState: (id: string) => void;
  getStopRowEditCallbacks: () => IterableIterator<() => void>;
};

const initializeEditingStore: StateCreatorWithImmer<EditingState> = immer((set, get) => ({
  transition: new Set(),
  edit: new Set(),
  debounced: new Set(),
  rowEditing: new Map(),
  stopRowEditCallbacks: new Map(),
  pushEditState: (type, id) => {
    set((state) => {
      state[type].add(id);
    });
  },
  releaseEditState: (type, id) => {
    set((state) => {
      state[type].delete(id);
    });
  },
  pushGridEditState: (id, state) => {
    set((currentState) => {
      const existingState = currentState.rowEditing.get(id);

      currentState.rowEditing.set(id, { initial: existingState?.initial ?? state, final: state });
    });
  },
  releaseGridEditState: (id) => {
    set((currentState) => {
      currentState.rowEditing.delete(id);
    });
  },
  getIsSavingEdits: () => {
    const state = get();
    return state.edit.size > 0 || state.debounced.size > 0;
  },
  getIsTransitioning: () => {
    const state = get();
    return state.transition.size > 0;
  },
  getIsActive: () => {
    const state = get();
    return state.getIsSavingEdits() || state.getIsTransitioning() || state.getHasRowEdits();
  },
  getHasRowEdits: () => {
    const state = get();
    for (const row of state.rowEditing.values()) {
      for (const id of Object.keys(row.initial)) {
        if (row.final[id] && !_.isEqual(row.initial[id], row.final[id])) return true;
      }
    }

    return false;
  },
  setStopRowEditCallback: (id, callback) => {
    set((state) => {
      state.stopRowEditCallbacks.set(id, callback);
    });
  },
  getStopRowEditCallbacks: () => {
    const state = get();
    return state.stopRowEditCallbacks.values();
  },
  cleanupStopRowEditCallbacks: (gridKey) => {
    set((state) => {
      state.stopRowEditCallbacks.delete(gridKey);
    });
  },
}));

const EditingStateContext = createContext<StoreApi<EditingState> | null>(null);

/**
 * ONE store per route, MANY buckets inside it — deliberately.
 *
 *   route ──▶ store            keyed on pathname (cleanup on navigation)
 *     └─ grid A ──▶ bucket     keyed on that grid's OWN `gridKey`
 *     └─ grid B ──▶ bucket
 *
 * The store is page-scoped because `useAwaitEdits` / `useTransitionCallback`
 * must wait on EVERY grid on the page — a per-grid store would let "Submit
 * Order" fire while a second grid still held unsaved edits. What must never be
 * shared is the BUCKET, which is why `gridKey` is a required prop.
 */
export default function EditingStateProvider({ children, scopeKey }: PropsWithChildren<{ scopeKey?: string }>) {
  const pathname = useLocation({ select: (location) => location.pathname });

  // since the editing state is in the main content layhout, we need to clean the store when the route changes.
  return <EditingStateProviderInner key={scopeKey ?? pathname}>{children}</EditingStateProviderInner>;
}

function EditingStateProviderInner({ children }: PropsWithChildren) {
  const storeRef = useRef(createStore(initializeEditingStore));

  return <EditingStateContext.Provider value={storeRef.current}>{children}</EditingStateContext.Provider>;
}

export function useEditingState<T>(selector: (state: EditingState) => T): T {
  const store = useContext(EditingStateContext);

  if (!store) {
    throw new Error('Missing EditingStateProvider context.');
  }

  return useStore(store, selector);
}

export function useEditStateCallback<T = unknown, FuncArgs extends unknown[] = unknown[]>(
  type: EditStateType,
  func: (...props: FuncArgs) => Promise<T>
): (...props: FuncArgs) => Promise<T> {
  const pushEditState = useEditingState((state) => state.pushEditState);
  const releaseEditState = useEditingState((state) => state.releaseEditState);

  return useCallback(
    (...args: FuncArgs) => {
      const id = v4();
      pushEditState(type, id);

      return func(...args).finally(() => {
        releaseEditState(type, id);
      });
    },
    [pushEditState, releaseEditState, func, type]
  );
}

export function useEditStateMutation<TData = any, TVariables = Record<string, any>>(
  type: EditStateType,
  ...mutationArgs: Parameters<typeof useMutation<TData, TVariables>>
): ReturnType<typeof useMutation<TData, TVariables>> {
  const [mutate, result] = useMutation<TData, TVariables>(...mutationArgs);
  const wrappedMutation = useEditStateCallback(type, mutate);

  return [wrappedMutation, result];
}

export function useEditStateDebounce(
  func: (...args: any[]) => void,
  delay: number,
  abortSignal?: MutableRefObject<AbortSignal>,
  deps: any[] = []
) {
  const pushEditState = useEditingState((state) => state.pushEditState);
  const releaseEditState = useEditingState((state) => state.releaseEditState);
  const debounceId = useMemo(() => v4(), []);

  const onScheduled = useCallback(() => pushEditState('debounced', debounceId), [pushEditState, debounceId]);
  const onReleased = useCallback(() => releaseEditState('debounced', debounceId), [releaseEditState, debounceId]);

  return useDebounce(func, delay, abortSignal, deps, onScheduled, onReleased);
}

/**
 * A debounce hook that maintains separate timers per key, integrated with EditingStateProvider.
 * Each key gets its own debounce timer and editing state tracking.
 *
 * Useful for scenarios like custom fields where typing in one field shouldn't cancel
 * the debounce of another field.
 *
 * @param func - The function to call with (key, value) after debounce.
 * @param delay - The debounce delay in milliseconds.
 * @param deps - Dependencies for the callback function.
 */
export function useEditStateDebounceByKey<T>(
  func: (key: string, value: T) => void,
  delay: number,
  deps: any[] = []
): (key: string, value: T) => void {
  const pushEditState = useEditingState((state) => state.pushEditState);
  const releaseEditState = useEditingState((state) => state.releaseEditState);
  const keyPrefix = useMemo(() => v4(), []);

  const onScheduled = useCallback(
    (key: string) => pushEditState('debounced', `${keyPrefix}:${key}`),
    [pushEditState, keyPrefix]
  );

  const onReleased = useCallback(
    (key: string) => releaseEditState('debounced', `${keyPrefix}:${key}`),
    [releaseEditState, keyPrefix]
  );

  return useDebounceByKey(func, delay, deps, onScheduled, onReleased);
}

/**
 * `useFieldCommit` wired into this provider — the One Law hook's counterpart to
 * `useEditStateDebounce`.
 *
 *   typed-but-uncommitted draft ──▶ 'debounced' edit state ──▶ transitions WAIT
 *
 * Without this bridge a field's local draft is invisible to `useAwaitEdits`, so
 * "Submit Order" could fire while the user's last keystrokes were still pending.
 */
export function useEditStateFieldCommit<T>(options: UseFieldCommitOptions<T>): FieldCommitHandlers<T> {
  const pushEditState = useEditingState((state) => state.pushEditState);
  const releaseEditState = useEditingState((state) => state.releaseEditState);
  const draftId = useMemo(() => v4(), []);

  const onPendingChange = useCallback(
    (isPending: boolean) => {
      if (isPending) {
        pushEditState('debounced', draftId);
      } else {
        releaseEditState('debounced', draftId);
      }
    },
    [pushEditState, releaseEditState, draftId]
  );

  return useFieldCommit({ ...options, onPendingChange });
}

export function useAwaitEdits(): () => [Promise<void>, boolean] {
  const store = useContext(EditingStateContext);
  const getIsSavingEdits = useEditingState((state) => state.getIsSavingEdits);
  const getHasRowEdits = useEditingState((state) => state.getHasRowEdits);
  if (!store) throw new Error('Missing EditingStateProvider context.');

  return useCallback(() => {
    const immediate = !getIsSavingEdits() && !getHasRowEdits();
    if (immediate) {
      return [Promise.resolve(), true];
    }

    return [
      new Promise((resolve) => {
        const unsubscribe = store.subscribe((state) => {
          if (!state.getIsSavingEdits() && !state.getHasRowEdits()) {
            unsubscribe();
            resolve();
          }
        });
      }),
      false,
    ];
  }, [store, getIsSavingEdits, getHasRowEdits]);
}

export function useTransitionCallback(callback: () => Promise<unknown | void>) {
  const waitForEdits = useAwaitEdits();
  const getStopRowEditCallbacks = useEditingState((state) => state.getStopRowEditCallbacks);
  const editBlockedRequest = useCallback(() => {
    const [awaitEdits, immediate] = waitForEdits();

    for (const callback of getStopRowEditCallbacks()) {
      callback();
    }

    const toast = immediate ? null : enqueueSnackbar('Waiting for changes to save...', { variant: 'default' });

    return awaitEdits.then(() => {
      if (toast) {
        closeSnackbar(toast);
      }

      return callback();
    });
  }, [callback, waitForEdits, getStopRowEditCallbacks]);

  return useEditStateCallback('transition', editBlockedRequest);
}

/** `gridKey` is REQUIRED: two grids that resolve to the same key share one `rowEditing` entry, and the first to release clears edits the second still has open — leaving 'Waiting for changes to save...' on screen with nothing left to save it. */
export function useRowEditSubscriptions(gridKey: string, apiRef: GridApiRef) {
  const pushEditState = useEditingState((state) => state.pushGridEditState);
  const releaseEditingState = useEditingState((state) => state.releaseGridEditState);

  useEffect(() => {
    // this could be optimized by using useGridSelector, but that needs to be nested within the DataGrid.
    return apiRef.current?.subscribeEvent('stateChange', (state) => {
      if (Object.values(state.editRows).length === 0) {
        releaseEditingState(gridKey);
      } else {
        pushEditState(gridKey, state.editRows);
      }
    });
  }, [apiRef, gridKey, pushEditState, releaseEditingState]);

  const setStopRowEditCallback = useEditingState((state) => state.setStopRowEditCallback);
  const cleanupStopRowEditCallbacks = useEditingState((state) => state.cleanupStopRowEditCallbacks);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    setStopRowEditCallback(gridKey, () => {
      const rows = api.state.editRows;

      for (const rowId in rows) {
        if (api.getRowMode(rowId) === 'edit') {
          api.stopRowEditMode({ id: rowId });
        }
      }
    });

    return () => {
      cleanupStopRowEditCallbacks(gridKey);
    };
  }, [apiRef, gridKey, setStopRowEditCallback, cleanupStopRowEditCallbacks]);
}
